import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { useAuthStore } from '@/stores/auth'
import { authApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Bot, ArrowRight, ChevronDown, ChevronUp, Mail, Lock } from 'lucide-react'

type Mode = 'login' | 'register'
type AuthMethod = 'magic' | 'password'

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('login')
  const [authMethod, setAuthMethod] = useState<AuthMethod>('magic')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [apiKeyValue, setApiKeyValue] = useState('')

  const { setSession, setApiKey } = useAuthStore()

  async function handleMagicLink(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await authApi.requestMagicLink(email)
      setMagicLinkSent(true)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send magic link')
    } finally {
      setLoading(false)
    }
  }

  async function handlePasswordSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      if (mode === 'register') {
        if (!name.trim()) {
          setError('Name is required')
          setLoading(false)
          return
        }
        const { user, org } = await authApi.register({ email, password, name })
        setSession(user, [org], org)
      } else {
        const { user, orgs, activeOrg } = await authApi.login({ email, password })
        setSession(user, orgs, activeOrg)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
      setLoading(false)
    }
  }

  function handleApiKeySubmit(e: FormEvent) {
    e.preventDefault()
    if (!apiKeyValue.startsWith('ra_')) {
      setError('API key must start with ra_')
      return
    }
    setApiKey(apiKeyValue)
  }

  function resetForm() {
    setError('')
    setMagicLinkSent(false)
  }

  // Magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-bg-base bg-geometric relative overflow-hidden flex items-center justify-center">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/4 -left-32 w-64 h-64 border border-accent/5 rounded-full" />
          <div className="absolute bottom-1/4 -right-20 w-48 h-48 border border-secondary/5 rotate-45" />
        </div>

        <div className="w-full max-w-sm relative z-10 mx-auto">
          <div className="flex flex-col items-center mb-10">
            <div className="w-14 h-14 rounded-[var(--radius-md)] bg-accent-muted flex items-center justify-center mb-5 accent-glow">
              <Mail className="w-7 h-7 text-accent" />
            </div>
            <h1 className="text-xl font-semibold text-text-primary tracking-tight">Check your email</h1>
            <p className="text-sm text-text-muted mt-2 text-center leading-relaxed">
              We sent a sign-in link to<br />
              <span className="text-text-secondary font-medium">{email}</span>
            </p>
          </div>

          <div className="glass-elevated p-7 text-center space-y-4">
            <p className="text-[13px] text-text-muted">
              Click the link in your email to sign in. The link expires in 15 minutes.
            </p>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => { setMagicLinkSent(false); setLoading(false) }}
                className="w-full"
              >
                Use a different email
              </Button>
              <button
                type="button"
                onClick={() => { setAuthMethod('password'); setMagicLinkSent(false) }}
                className="text-xs text-text-muted hover:text-text-secondary transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Sign in with password instead
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-bg-base bg-geometric relative overflow-hidden flex items-center justify-center">
      {/* Decorative geometry */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/4 -left-32 w-64 h-64 border border-accent/5 rounded-full" />
        <div className="absolute bottom-1/4 -right-20 w-48 h-48 border border-secondary/5 rotate-45" />
        <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-accent/20 rounded-full" />
        <div className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-secondary/20 rounded-full" />
      </div>

      <div className="w-full max-w-sm relative z-10 mx-auto">
        {/* Brand */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-[var(--radius-md)] bg-accent-muted flex items-center justify-center mb-5 accent-glow">
            <Bot className="w-7 h-7 text-accent" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary tracking-tight">Relay Agent</h1>
          <p className="text-sm text-text-muted mt-1">Voice AI Console</p>
        </div>

        {/* Mode toggle */}
        <div className="flex gap-1 p-1 glass-elevated rounded-[var(--radius-sm)] mb-5">
          <button
            type="button"
            onClick={() => { setMode('login'); resetForm() }}
            className={`flex-1 text-[13px] font-medium py-2 rounded-[var(--radius-sm)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              mode === 'login'
                ? 'bg-accent text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            Sign In
          </button>
          <button
            type="button"
            onClick={() => { setMode('register'); setAuthMethod('password'); resetForm() }}
            className={`flex-1 text-[13px] font-medium py-2 rounded-[var(--radius-sm)] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              mode === 'register'
                ? 'bg-accent text-white'
                : 'text-text-muted hover:text-text-secondary'
            }`}
          >
            Create Account
          </button>
        </div>

        {/* Auth method toggle (sign-in only) */}
        {mode === 'login' && (
          <div className="flex gap-3 mb-5 justify-center">
            <button
              type="button"
              onClick={() => { setAuthMethod('magic'); resetForm() }}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                authMethod === 'magic'
                  ? 'bg-bg-surface text-text-primary border border-border'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              <Mail className="w-3 h-3" />
              Magic Link
            </button>
            <button
              type="button"
              onClick={() => { setAuthMethod('password'); resetForm() }}
              className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                authMethod === 'password'
                  ? 'bg-bg-surface text-text-primary border border-border'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              <Lock className="w-3 h-3" />
              Password
            </button>
          </div>
        )}

        {/* Magic Link Form */}
        {mode === 'login' && authMethod === 'magic' ? (
          <form onSubmit={handleMagicLink} className="glass-elevated p-7 space-y-4">
            <div>
              <label className="text-[13px] font-medium text-text-secondary block mb-2">
                Email
              </label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError('') }}
                autoFocus
              />
            </div>

            {error && (
              <p className="text-xs text-error flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-error" />
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading || !email}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-bg-base/30 border-t-bg-base rounded-full animate-spin" />
                  Sending link...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Send Magic Link
                </span>
              )}
            </Button>
          </form>
        ) : (
          /* Password Form */
          <form onSubmit={handlePasswordSubmit} className="glass-elevated p-7 space-y-4">
            {mode === 'register' && (
              <div>
                <label className="text-[13px] font-medium text-text-secondary block mb-2">
                  Name
                </label>
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => { setName(e.target.value); setError('') }}
                  autoFocus={mode === 'register'}
                />
              </div>
            )}

            <div>
              <label className="text-[13px] font-medium text-text-secondary block mb-2">
                Email
              </label>
              <Input
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError('') }}
                autoFocus={mode === 'login'}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[13px] font-medium text-text-secondary">
                  Password
                </label>
                {mode === 'login' && (
                  <Link
                    to="/auth/forgot-password"
                    className="text-[12px] text-accent hover:text-accent/80 transition-colors"
                  >
                    Forgot password?
                  </Link>
                )}
              </div>
              <Input
                type="password"
                placeholder={mode === 'register' ? 'Min 8 characters' : 'Enter password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError('') }}
              />
            </div>

            {error && (
              <p className="text-xs text-error flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-error" />
                {error}
              </p>
            )}

            <Button type="submit" className="w-full" disabled={loading || !email || !password}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-bg-base/30 border-t-bg-base rounded-full animate-spin" />
                  {mode === 'register' ? 'Creating account...' : 'Signing in...'}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {mode === 'register' ? 'Create Account' : 'Sign In'}
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>
        )}

        {/* API Key fallback */}
        <div className="mt-5">
          <button
            type="button"
            onClick={() => setShowApiKey(!showApiKey)}
            className="w-full flex items-center justify-center gap-2 text-xs text-text-muted hover:text-text-secondary transition-colors rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Use API Key instead
            {showApiKey ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {showApiKey && (
            <form onSubmit={handleApiKeySubmit} className="mt-3 glass-elevated p-5 space-y-4">
              <div>
                <label className="text-[13px] font-medium text-text-secondary block mb-2">
                  API Key
                </label>
                <Input
                  type="password"
                  placeholder="ra_..."
                  value={apiKeyValue}
                  onChange={(e) => { setApiKeyValue(e.target.value); setError('') }}
                />
              </div>
              <Button type="submit" variant="outline" className="w-full" disabled={!apiKeyValue}>
                <span className="flex items-center gap-2">
                  Connect with API Key
                  <ArrowRight className="w-4 h-4" />
                </span>
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
